/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  RefreshControl,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { useScreenLoad } from '../../shared/ui/hooks-screen/useScreenLoad';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState';
import { formatRelativeDayTime } from '@shared/vueltoDateTime';
import { getSessionOrganizationId } from '../../shell/session/sessionOrganization';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { supabase } from '../../shared/supabase/supabaseClient';
import {
  listOrderConversationsForOrg,
  type OrderConversationFilter,
  type OrderConversationListItem,
} from '../infra/orderMessagesRepo';

const FILTERS: { key: OrderConversationFilter; label: string }[] = [
  { key: 'unread', label: 'No leídos' },
  { key: 'open', label: 'Abiertos' },
  { key: 'all', label: 'Todos' },
];

type OrderMessagesInboxScreenProps = {
  /** When embedded in OrdersInbox, pass `OrdersInbox` so the web header refresh button works. */
  refreshRouteName?: string;
};

export function OrderMessagesInboxScreen({
  refreshRouteName = 'OrdersInbox',
}: OrderMessagesInboxScreenProps = {}) {
  const navigation = useNavigation<any>();
  const [rows, setRows] = useState<OrderConversationListItem[]>([]);
  const [filter, setFilter] = useState<OrderConversationFilter>('unread');
  const [initialLoading, setInitialLoading] = useState(true);
  const currentBranchId = useSessionStore((s) => s.currentBranchId);

  const load = useCallback(async () => {
    try {
      const orgId = await getSessionOrganizationId();
      if (!orgId) {
        setRows([]);
        return;
      }
      const data = await listOrderConversationsForOrg(orgId, filter, currentBranchId);
      setRows(data);
    } finally {
      setInitialLoading(false);
    }
  }, [filter, currentBranchId]);

  const { refreshing, onRefresh } = useScreenLoad(refreshRouteName, load, { feedback: false });

  const skipFilterReload = useRef(true);
  useEffect(() => {
    if (skipFilterReload.current) {
      skipFilterReload.current = false;
      return;
    }
    setInitialLoading(true);
    void load();
  }, [filter, load]);

  // BUG #4 fix: Realtime subscription to order_messages so new customer messages
  // appear without manual pull-to-refresh. Debounced 500ms to avoid spam on
  // multi-message bursts.
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let timerRef: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const scheduleRefresh = () => {
      if (timerRef) clearTimeout(timerRef);
      timerRef = setTimeout(() => void loadRef.current(), 500);
    };

    void (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token || cancelled) return;
      await supabase.realtime.setAuth(session.access_token);
      if (cancelled) return;

      channel = supabase
        .channel('orders-msg-inbox-realtime')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'order_messages',
          },
          scheduleRefresh,
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'order_conversations',
          },
          scheduleRefresh,
        )
        .subscribe((status) => {
          if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            scheduleRefresh();
          }
        });
    })();

    return () => {
      cancelled = true;
      if (timerRef) clearTimeout(timerRef);
      if (channel) void supabase.removeChannel(channel);
    };
  }, []);

  return (
    <View style={styles.wrap}>
      <View style={styles.filters}>
        {FILTERS.map((f) => (
          <TouchableOpacity
            key={f.key}
            accessibilityRole="button"
            accessibilityLabel={`Filtrar por ${f.label}`}
            style={[styles.chip, filter === f.key && styles.chipActive]}
            onPress={() => setFilter(f.key)}
          >
            <Text style={[styles.chipText, filter === f.key && styles.chipTextActive]}>{f.label}</Text>
          </TouchableOpacity>
        ))}
      </View>
      {initialLoading && rows.length === 0 ? (
        <ActivityIndicator color={ticketColors.ink2} style={{ marginTop: 24 }} />
      ) : (
        <FlatList
          data={rows}
          keyExtractor={(item) => item.id}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
          contentContainerStyle={styles.list}
          ListEmptyComponent={
            <ModuleEmptyState
              title="No hay mensajes"
              subtitle={
                filter === 'unread'
                  ? 'Cuando un cliente escriba, aparece acá.'
                  : 'Probá con otro filtro.'
              }
              testID="order-messages.empty"
            />
          }
          renderItem={({ item }) => {
            const hasCustomerPreview = Boolean(item.last_message?.trim());
            const unread = !item.read_by_store_at && hasCustomerPreview;
            return (
              <TouchableOpacity
                accessibilityRole="button"
                accessibilityLabel={`Abrir conversación de ${item.customer_name || 'cliente'}`}
                style={[styles.card, unread && styles.cardUnread]}
                onPress={() =>
                  navigation.navigate('OrderMessageThread', {
                    orderId: item.order_id,
                    conversationId: item.id,
                  })
                }
              >
                <View style={styles.cardHeader}>
                  <Text style={styles.cardTitle} numberOfLines={1}>
                    {item.customer_name || 'Cliente'} · #{item.order_id.slice(0, 8)}
                  </Text>
                  {unread ? <View style={styles.dot} /> : null}
                </View>
                <Text style={[styles.preview, !hasCustomerPreview && styles.previewMuted]} numberOfLines={2}>
                  {hasCustomerPreview ? item.last_message : 'Todavía no hay mensajes'}
                </Text>
                {item.last_message_at ? (
                  <Text style={styles.meta}>{formatRelativeDayTime(item.last_message_at)}</Text>
                ) : null}
              </TouchableOpacity>
            );
          }}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { flex: 1 },
  filters: { flexDirection: 'row', gap: 8, paddingHorizontal: 16, paddingVertical: 10 },
  chip: {
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  chipActive: { backgroundColor: ticketColors.ink, borderColor: ticketColors.ink },
  chipText: { fontSize: 13, color: ticketColors.ink, fontFamily: ticketFonts.sans },
  chipTextActive: { color: ticketColors.paper },
  list: { padding: 16, gap: 10, flexGrow: 1 },
  card: {
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    padding: 12,
    backgroundColor: ticketColors.paper,
    marginBottom: 10,
  },
  cardUnread: { borderColor: ticketColors.gold },
  cardHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  cardTitle: { flex: 1, fontWeight: '800', color: ticketColors.ink, fontFamily: ticketFonts.sans },
  dot: { width: 8, height: 8, borderRadius: 4, backgroundColor: ticketColors.gold },
  preview: { marginTop: 6, color: ticketColors.ink2, fontFamily: ticketFonts.sans },
  previewMuted: { color: ticketColors.ink3, fontStyle: 'italic' },
  meta: { marginTop: 6, fontSize: 12, color: ticketColors.ink3, fontFamily: ticketFonts.sans },
});
